import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Plus,
  Droplets,
  Loader2,
  AlertTriangle,
  Download,
  Eye,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fmtTz } from "../lib/time";

const SUBSTANCE_TYPES = [
  'Oil/Petroleum',
  'Chemical',
  'Fuel',
  'Coolant',
  'Hydraulic Fluid',
  'Paint/Coating',
  'Sewage',
  'Other'
];

export default function AdminSpillReports() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const [formData, setFormData] = useState({
    date_time: '',
    location: '',
    project_id: '',
    substance_type: '',
    estimated_quantity: '',
    cause: '',
    immediate_actions: '',
    containment_method: '',
    injuries: false,
    injury_details: '',
    environmental_impact: false,
    environmental_details: '',
    corrective_actions: '',
    supervisor_notified: false,
    regulatory_reporting_required: false,
    signature: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, projRes] = await Promise.all([
        authAxios.get('/forms/spill-reports'),
        authAxios.get('/projects')
      ]);
      setReports(reportsRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      date_time: '',
      location: '',
      project_id: '',
      substance_type: '',
      estimated_quantity: '',
      cause: '',
      immediate_actions: '',
      containment_method: '',
      injuries: false,
      injury_details: '',
      environmental_impact: false,
      environmental_details: '',
      corrective_actions: '',
      supervisor_notified: false,
      regulatory_reporting_required: false,
      signature: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date_time || !formData.location || !formData.substance_type || !formData.signature) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await authAxios.post('/forms/spill-reports', {
        ...formData,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null
      });
      toast.success('Spill report submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (reportId) => {
    try {
      const response = await authAxios.get(`/pdf/spill-report/${reportId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `spill_report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="spill-reports">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Spill Reports</h1>
          <p className="text-stone-500 mt-1">Document and track spill incidents</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-spill-btn">
              <Plus className="w-5 h-5 mr-2" />
              Report Spill
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-blue-600" />
                Spill Incident Report Form
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Incident Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date & Time *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.date_time}
                      onChange={(e) => setFormData({ ...formData, date_time: e.target.value })}
                      required
                      data-testid="spill-datetime"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Project (Optional)</Label>
                    <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                      <SelectTrigger data-testid="spill-project">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Specific location of the spill"
                    required
                    data-testid="spill-location"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Substance Type *</Label>
                    <Select value={formData.substance_type} onValueChange={(v) => setFormData({ ...formData, substance_type: v })}>
                      <SelectTrigger data-testid="spill-substance">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUBSTANCE_TYPES.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Quantity *</Label>
                    <Input
                      value={formData.estimated_quantity}
                      onChange={(e) => setFormData({ ...formData, estimated_quantity: e.target.value })}
                      placeholder="e.g., 5 gallons, 1 liter"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Cause *</Label>
                  <Textarea
                    value={formData.cause}
                    onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
                    placeholder="Describe what caused the spill..."
                    rows={2}
                    required
                  />
                </div>
              </div>

              {/* Response Actions */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Response Actions</h3>
                <div className="space-y-2">
                  <Label>Immediate Actions Taken *</Label>
                  <Textarea
                    value={formData.immediate_actions}
                    onChange={(e) => setFormData({ ...formData, immediate_actions: e.target.value })}
                    placeholder="What actions were taken immediately..."
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Containment Method *</Label>
                  <Textarea
                    value={formData.containment_method}
                    onChange={(e) => setFormData({ ...formData, containment_method: e.target.value })}
                    placeholder="How was the spill contained..."
                    rows={2}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Corrective Actions *</Label>
                  <Textarea
                    value={formData.corrective_actions}
                    onChange={(e) => setFormData({ ...formData, corrective_actions: e.target.value })}
                    placeholder="Steps to prevent future incidents..."
                    rows={2}
                    required
                  />
                </div>
              </div>

              {/* Impact Assessment */}
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Impact Assessment</h3>
                
                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <Label>Were there any injuries?</Label>
                    <p className="text-sm text-stone-500">Report any personnel injuries</p>
                  </div>
                  <Switch
                    checked={formData.injuries}
                    onCheckedChange={(v) => setFormData({ ...formData, injuries: v })}
                  />
                </div>
                {formData.injuries && (
                  <Textarea
                    value={formData.injury_details}
                    onChange={(e) => setFormData({ ...formData, injury_details: e.target.value })}
                    placeholder="Describe injuries..."
                    rows={2}
                  />
                )}

                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <Label>Environmental Impact?</Label>
                    <p className="text-sm text-stone-500">Did the spill affect the environment?</p>
                  </div>
                  <Switch
                    checked={formData.environmental_impact}
                    onCheckedChange={(v) => setFormData({ ...formData, environmental_impact: v })}
                  />
                </div>
                {formData.environmental_impact && (
                  <Textarea
                    value={formData.environmental_details}
                    onChange={(e) => setFormData({ ...formData, environmental_details: e.target.value })}
                    placeholder="Describe environmental impact..."
                    rows={2}
                  />
                )}

                <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                  <div>
                    <Label>Supervisor Notified?</Label>
                  </div>
                  <Switch
                    checked={formData.supervisor_notified}
                    onCheckedChange={(v) => setFormData({ ...formData, supervisor_notified: v })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <Label className="text-amber-800">Regulatory Reporting Required?</Label>
                    <p className="text-sm text-amber-600">Does this require reporting to authorities?</p>
                  </div>
                  <Switch
                    checked={formData.regulatory_reporting_required}
                    onCheckedChange={(v) => setFormData({ ...formData, regulatory_reporting_required: v })}
                  />
                </div>
              </div>

              {/* Signature */}
              <div className="space-y-2">
                <Label>Signature (Type Name) *</Label>
                <Input
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  placeholder="Type your full name as signature"
                  required
                  data-testid="spill-signature"
                />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit Report
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Reports Table */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-500" />
            Spill Reports ({reports.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <Droplets className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No spill reports found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Time</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Substance</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Reported By</TableHead>
                    <TableHead>Impact</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right sticky right-0 bg-white">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>{fmtTz(report.date_time, 'MMM d, yyyy h:mm a')}</TableCell>
                      <TableCell className="font-medium">{report.location}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{report.substance_type}</Badge>
                      </TableCell>
                      <TableCell>{report.estimated_quantity}</TableCell>
                      <TableCell>{report.reported_by_name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {report.injuries && <Badge className="bg-rose-100 text-rose-700">Injuries</Badge>}
                          {report.environmental_impact && <Badge className="bg-amber-100 text-amber-700">Env.</Badge>}
                          {report.regulatory_reporting_required && <Badge className="bg-purple-100 text-purple-700">Reg.</Badge>}
                          {!report.injuries && !report.environmental_impact && !report.regulatory_reporting_required && '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={report.status === 'reviewed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {report.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1 sticky right-0 bg-white whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => { setSelectedReport(report); setViewDialogOpen(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => exportPDF(report.id)}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={async () => {
                              if (!window.confirm('Delete this spill report permanently?')) return;
                              try {
                                await authAxios.delete(`/forms/spill-reports/${report.id}`);
                                toast.success('Spill report deleted');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
                            }}
                            data-testid={`delete-spill-${report.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Report Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplets className="w-5 h-5 text-blue-600" />
              Spill Report Details
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Date/Time</p>
                  <p className="font-medium">{fmtTz(selectedReport.date_time, 'MMM d, yyyy h:mm a')}</p>
                </div>
                <div>
                  <p className="text-stone-500">Location</p>
                  <p className="font-medium">{selectedReport.location}</p>
                </div>
                <div>
                  <p className="text-stone-500">Substance</p>
                  <p className="font-medium">{selectedReport.substance_type}</p>
                </div>
                <div>
                  <p className="text-stone-500">Quantity</p>
                  <p className="font-medium">{selectedReport.estimated_quantity}</p>
                </div>
              </div>

              <div className="text-sm">
                <p className="text-stone-500">Cause</p>
                <p>{selectedReport.cause}</p>
              </div>

              <div className="text-sm">
                <p className="text-stone-500">Immediate Actions</p>
                <p>{selectedReport.immediate_actions}</p>
              </div>

              <div className="text-sm">
                <p className="text-stone-500">Containment Method</p>
                <p>{selectedReport.containment_method}</p>
              </div>

              <div className="text-sm">
                <p className="text-stone-500">Corrective Actions</p>
                <p>{selectedReport.corrective_actions}</p>
              </div>

              {selectedReport.injuries && (
                <div className="p-3 bg-rose-50 rounded-lg text-sm">
                  <p className="font-medium text-rose-700">Injuries Reported</p>
                  <p className="text-rose-600">{selectedReport.injury_details}</p>
                </div>
              )}

              {selectedReport.environmental_impact && (
                <div className="p-3 bg-amber-50 rounded-lg text-sm">
                  <p className="font-medium text-amber-700">Environmental Impact</p>
                  <p className="text-amber-600">{selectedReport.environmental_details}</p>
                </div>
              )}

              <div className="flex gap-4 text-sm">
                <div>
                  <span className="text-stone-500">Supervisor Notified: </span>
                  <span className="font-medium">{selectedReport.supervisor_notified ? 'Yes' : 'No'}</span>
                </div>
                <div>
                  <span className="text-stone-500">Regulatory Reporting: </span>
                  <span className="font-medium">{selectedReport.regulatory_reporting_required ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className="pt-4 border-t text-sm">
                <p className="text-stone-500">Signature</p>
                <p className="font-medium italic">{selectedReport.signature}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
